import { useRef, useState, type PointerEvent } from 'react';
import {
  getOffset,
  swipeReducer,
  type SwipeEvent,
  type SwipeState,
} from '@/lib/swipe-reveal';

export type SwipeRevealState = 'open' | 'closed' | 'dragging';

/**
 * Pointer events → `swipeReducer` (design A5/A6, AD-026). Controlled: at rest
 * the `open` prop wins; releasing a drag on the other side of the threshold
 * calls `onOpenChange`. A vertical gesture never moves the row (the browser
 * scrolls, `touch-action: pan-y` on the element that gets `bind`).
 */
export function useSwipeReveal(opts: {
  actionsWidth: number;
  open: boolean;
  onOpenChange(open: boolean): void;
}): {
  bind: {
    onPointerDown(e: PointerEvent<HTMLElement>): void;
    onPointerMove(e: PointerEvent<HTMLElement>): void;
    onPointerUp(e: PointerEvent<HTMLElement>): void;
    onPointerCancel(e: PointerEvent<HTMLElement>): void;
  };
  offset: number;
  state: SwipeRevealState;
} {
  const { actionsWidth, open, onOpenChange } = opts;
  const cfg = { actionsWidth };
  const [gesture, setGesture] = useState<SwipeState>({ phase: 'rest', open });
  // Events can arrive faster than renders (move, move, up): read the latest.
  const gestureRef = useRef(gesture);

  const current = (s: SwipeState): SwipeState =>
    s.phase === 'rest' ? { phase: 'rest', open } : s;

  const send = (
    event: SwipeEvent,
    target?: HTMLElement,
    pointerId?: number
  ) => {
    const prev = current(gestureRef.current);
    const next = swipeReducer(prev, event, cfg);
    if (next === prev) return; // e.g. a mouse moving over the row at rest
    gestureRef.current = next;
    setGesture(next);
    if (
      next.phase === 'dragging' &&
      prev.phase !== 'dragging' &&
      target &&
      pointerId !== undefined &&
      typeof target.setPointerCapture === 'function'
    ) {
      target.setPointerCapture(pointerId);
    }
    if (next.phase === 'rest' && next.open !== open) onOpenChange(next.open);
  };

  const shown = current(gesture);
  const state: SwipeRevealState =
    shown.phase === 'dragging' ? 'dragging' : shown.open ? 'open' : 'closed';

  return {
    bind: {
      onPointerDown: (e) => send({ type: 'down', x: e.clientX, y: e.clientY }),
      onPointerMove: (e) =>
        send(
          { type: 'move', x: e.clientX, y: e.clientY },
          e.currentTarget,
          e.pointerId
        ),
      onPointerUp: () => send({ type: 'up' }),
      onPointerCancel: () => send({ type: 'cancel' }),
    },
    offset: getOffset(shown, cfg),
    state,
  };
}
